import { useStaffPermission } from "@/Hooks/useStaffPermission";
import { SETTING_PAGE_SUBROUTES } from "../../../Constants/setting";
import * as navbarIcons from "../../../Utils/SVGExports/NavbarIcons";
import { PARENT_TAB_ID, SUB_NAVLIST_IDS } from "../Constants/index";
import * as ROUTES from "@/Constants/routesConstant";
import { useSubscriptionDetailsSuite } from "@/Hooks/useSubscriptionDetailsSuite";
import { isStoreServiceBased, isStoreTypeB2b } from "@/Utils/common";
import { RootState, useSelector } from "@/Redux/Reducers";
import { STAFF_PERMISSION_TYPE } from "@/Constants";
import { NavListItemProps } from "../Types";
import { setParentTabVisibilityAndAccessabilityByChildTab } from "../Utils";

export function useNavLinkList() {
  const { isInternalDemoAdminAccess, subscriptionDetails, storeData } = useSelector((state: RootState) => ({
    isInternalDemoAdminAccess: state?.storeReducer?.store?.internal_admin_access || false,
    subscriptionDetails: state.themeReducer.subscriptionDetails,
    storeData: state.storeReducer.store,
  }));

  const { featureStatus, isNewEcommFlow } = useSubscriptionDetailsSuite();
  const { canManageOrders, canManageCatalog, canManageMarketing, canManageSubscriptions } = useStaffPermission();

  function getRequestNameByStoreType() {
    switch (true) {
      case isStoreTypeB2b():
        return "Orders";
      case isStoreServiceBased():
        return "Bookings";
      default:
        return "Orders";
    }
  }

  /**
   * 1. accessable - this key defines the accessability of tab(parent and child both)
   *    If any tab isn't accessable,  a request permission pop-up would be shown(requiredPermission).
   * 2. requiredPermission - this key define the required staff permission(1,2,3,4,5) for the tab.
   * 3. visible - this key define the visibility of a tab in navigation bar.
   */
  const navLinkList = [
    {
      id: PARENT_TAB_ID.HOME,
      requiredPermission: STAFF_PERMISSION_TYPE.ORDERS,
      name: "Home / " + getRequestNameByStoreType(),
      Icon: navbarIcons.HomeIcon,
      subNavList: [
        {
          id: SUB_NAVLIST_IDS.HOME.ORDER,
          accessable: canManageOrders,
          requiredPermission: STAFF_PERMISSION_TYPE.ORDERS,
          visible: true,
          name: getRequestNameByStoreType(),
          route: ROUTES.ORDERS_PAGE_ROUTE,
        },
        {
          id: SUB_NAVLIST_IDS.HOME.LEADS,
          accessable: canManageOrders,
          requiredPermission: STAFF_PERMISSION_TYPE.ORDERS,
          visible: true,
          name: "Leads",
          route: ROUTES.CARTS_PAGE_ROUTE,
          ecommDependency: featureStatus.abandonedCart?.subscriptionType,
        },
        {
          id: SUB_NAVLIST_IDS.HOME.STORE_ANALYTICS,
          accessable: canManageOrders,
          requiredPermission: STAFF_PERMISSION_TYPE.ORDERS,
          /** Hide  store analytics if domain hasn't been purchased &
           * no ecomm hasn't been purchased (but will be shown in ecomm expiry condition)  */
          visible: !!storeData?.domain_info?.site_id,
          name: "Store Analytics",
          route: ROUTES.STORE_ANALYTICS_ROUTE,
          ecommDependency: featureStatus.storeAnalytics?.subscriptionType,
        },
        {
          id: SUB_NAVLIST_IDS.HOME.OUT_OF_STOCK,
          accessable: canManageOrders,
          requiredPermission: STAFF_PERMISSION_TYPE.ORDERS,
          visible: true,
          name: "Out of stock query",
          route: ROUTES.OUT_OF_STOCK_ROUTE,
          ecommDependency: featureStatus.notifyMe?.subscriptionType,
        },
      ],
    },
    {
      id: PARENT_TAB_ID.CATALOG,
      requiredPermission: STAFF_PERMISSION_TYPE.CATALOG,
      name: "Catalog / Products",
      Icon: navbarIcons.CatalogIcon,
      subNavList: [
        {
          id: SUB_NAVLIST_IDS.CATALOG.PRODUCT,
          accessable: canManageCatalog,
          requiredPermission: STAFF_PERMISSION_TYPE.CATALOG,
          visible: true,
          name: "Products",
          route: ROUTES.CATALOG_PRODUCT,
        },
        {
          id: SUB_NAVLIST_IDS.CATALOG.COLLECTION,
          accessable: canManageCatalog,
          requiredPermission: STAFF_PERMISSION_TYPE.CATALOG,
          visible: true,
          name: "Collections",
          route: ROUTES.COLLECTIONS_LIST,
        },
        {
          id: SUB_NAVLIST_IDS.CATALOG.CUSTOMER_REVIEW,
          accessable: canManageCatalog,
          requiredPermission: STAFF_PERMISSION_TYPE.CATALOG,
          visible: true,
          name: "Customer Reviews",
          route: ROUTES.CUSTOMER_REVIEW_RATINGS_ROUTE,
        },
        {
          id: SUB_NAVLIST_IDS.CATALOG.WISHLISTS,
          accessable: canManageCatalog,
          requiredPermission: STAFF_PERMISSION_TYPE.CATALOG,
          visible: true,
          name: "Wishlists",
          route: ROUTES.WISHLISTS_ROUTE,
          Icon: navbarIcons.NewTagIcon,
        },
      ],
    },
    {
      id: PARENT_TAB_ID.GROW,
      accessable: canManageSubscriptions,
      requiredPermission: STAFF_PERMISSION_TYPE.SUBSCRIPTION,
      visible: true,
      name: "Grow",
      Icon: navbarIcons.GrowIcon,
      route: ROUTES.PREMIUM_PAGE,
    },
    {
      id: PARENT_TAB_ID.UTILITY,
      accessable: true,
      visible: true,
      name: "Utilities",
      Icon: navbarIcons.UtilityIcon,
      route: ROUTES.APP_UTILITIES,
    },
    {
      id: PARENT_TAB_ID.WEBSITE,
      name: "Website",
      Icon: navbarIcons.WebsiteIcon,
      subNavList: [
        {
          id: SUB_NAVLIST_IDS.WEBSITE.BUILDER,
          accessable: canManageSubscriptions,
          requiredPermission: STAFF_PERMISSION_TYPE.SUBSCRIPTION,
          visible: isInternalDemoAdminAccess,
          name: "Website builder",
          route: ROUTES.PAGE_BUILDER_ROUTE,
          openInNewTab: true,
        },
        {
          id: SUB_NAVLIST_IDS.WEBSITE.THEME_EDITOR,
          accessable: canManageSubscriptions,
          requiredPermission: STAFF_PERMISSION_TYPE.SUBSCRIPTION,
          visible: !isInternalDemoAdminAccess && !!subscriptionDetails?.theme,
          name: "Theme Editor",
          route: ROUTES.WEB_EDITOR_ROUTE,
        },
        {
          id: SUB_NAVLIST_IDS.WEBSITE.BLOGS,
          accessable: true,
          requiredPermission: STAFF_PERMISSION_TYPE.ORDERS,
          visible: !featureStatus?.aiBlogPage?.isLocked,
          name: "Blogs",
          route: ROUTES.BLOGS_DASHBOARD,
          ...isNewEcommFlow ? { ecommDependency: featureStatus.notifyMe?.subscriptionType } : {},
        },
        {
          id: SUB_NAVLIST_IDS.WEBSITE.ADDITIONAL_PAGES,
          accessable: canManageOrders,
          requiredPermission: STAFF_PERMISSION_TYPE.ORDERS,
          visible: featureStatus.additionalPage.isVisible,
          name: "Additional pages",
          route: ROUTES.ADDITIONAL_PAGES_ROUTE,
          ecommDependency: featureStatus.notifyMe?.subscriptionType,
        },
        {
          id: SUB_NAVLIST_IDS.WEBSITE.SEO,
          accessable: canManageOrders,
          requiredPermission: STAFF_PERMISSION_TYPE.ORDERS,
          visible: !featureStatus?.storeSeoDetails?.isLocked,
          name: "SEO",
          route: ROUTES.SEO_DASHBOARD,
          ecommDependency: featureStatus.notifyMe?.subscriptionType,
        },
      ],
    },
    {
      id: PARENT_TAB_ID.ECOMM,
      accessable: canManageSubscriptions,
      requiredPermission: STAFF_PERMISSION_TYPE.SUBSCRIPTION,
      visible: true,
      name: "eCommerce",
      Icon: navbarIcons.EcommerceIcon,
      route: ROUTES.SUBSCRIPTION,
    },
    {
      id: PARENT_TAB_ID.MARKETING,
      accessable: canManageMarketing,
      requiredPermission: STAFF_PERMISSION_TYPE.MARKETING,
      visible: true,
      name: "Marketing",
      Icon: navbarIcons.MarketingIcon,
      route: ROUTES.MARKETING_ROUTE,
    },
    {
      id: PARENT_TAB_ID.CUSTOMER,
      accessable: canManageMarketing,
      requiredPermission: STAFF_PERMISSION_TYPE.MARKETING,
      visible: true,
      name: "Customers",
      Icon: navbarIcons.CustomerIcon,
      route: ROUTES.CRM_ROUTE,
      ecommDependency: featureStatus.crm?.subscriptionType,
    },
    {
      id: PARENT_TAB_ID.SETTING,
      accessable: true,
      visible: true,
      name: "Settings",
      Icon: navbarIcons.SettingIcon,
      route: SETTING_PAGE_SUBROUTES.CONTROLS,
    },
  ] as NavListItemProps[];

  setParentTabVisibilityAndAccessabilityByChildTab(navLinkList);

  const navLinkListIdMap = navLinkList.reduce((result, navLinkData) => {
    result[navLinkData.id] = navLinkData;
    return result;
  }, {});

  return { navLinkList, navLinkListIdMap };
}
